import {
  HHButton,
  HHDivider,
  HHError,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Skeleton } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useGetAllDiscountsPaginated } from '../../hooks';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import {
  useCreateDiscountMutation,
  useGetAllPackagesQuery,
  useGetDiscountUsageLazyQuery,
} from '../../types';
import { computeEffectiveRange } from '../../utils/date-helper';
import { DiscountType, toScopeInputs } from '../../utils/discount-utils';
import { DiscountForm } from '../discounts/form/form';
import { ParsedDiscountValues } from '../discounts/form/form-constants';
import { ScopeItem } from '../form-fields/scope-selector/scope-selector-constants';
import { ScopeDisplay } from '../scope-display/scope-display';

export interface ProgramCapCreditsProps {
  clientId: number;
  inquiryDate: string;
  isDisabled: boolean;
}

export interface CombinedDiscountData {
  startDate: string;
  endDate: string;
  discount: number;
  discountDetailId: number;
  discountUsage: number;
  scopes: ScopeItem[];
}

export const PROGRAM_CAP_CREDITS_SKELETON_TEST_ID =
  'program-cap-credits-skeleton-section';

const SkeletonField = ({
  labelWidth,
  valueWidth,
  flex,
}: {
  labelWidth: number;
  valueWidth: number;
  flex: number;
}): JSX.Element => (
  <HHStack flex={flex}>
    <Skeleton variant="text" width={labelWidth} height={24} />
    <Skeleton variant="text" width={valueWidth} height={20} />
  </HHStack>
);

const CreditRowSkeleton = (): JSX.Element => (
  <HHStack
    direction="row"
    spacing={4}
    width="100%"
    data-testid={PROGRAM_CAP_CREDITS_SKELETON_TEST_ID}
  >
    <SkeletonField flex={2} labelWidth={110} valueWidth={160} />
    <SkeletonField flex={1} labelWidth={60} valueWidth={120} />
    <SkeletonField flex={1} labelWidth={100} valueWidth={60} />
  </HHStack>
);

// ACCOUNTS-AND-PLAN-MIGRATION: clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
export const ProgramCapCredits = ({
  clientId,
  inquiryDate,
  isDisabled = false,
}: ProgramCapCreditsProps): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();
  const {
    data: packagesData,
    loading: packagesLoading,
    error: packagesError,
  } = useGetAllPackagesQuery();
  const [createDiscount] = useCreateDiscountMutation();

  const {
    data: discounts,
    isLoading,
    error,
    refetch,
  } = useGetAllDiscountsPaginated({
    clientId,
    discountType: DiscountType.programCapCredits,
    inquiryDate,
  });

  const detailIds = useMemo(
    () =>
      discounts.flatMap(d =>
        d.discountDetails.map(detail => detail.discountDetailId),
      ),
    [discounts],
  );

  const [
    getDiscountUsage,
    {
      data: discountUsageData,
      loading: discountUsageLoading,
      error: discountUsageError,
    },
  ] = useGetDiscountUsageLazyQuery();

  useEffect(() => {
    if (detailIds.length) {
      getDiscountUsage({
        variables: { discountDetailIds: detailIds },
      });
    }
  }, [getDiscountUsage, detailIds]);

  const isDiscountUsagePending =
    detailIds.length > 0 && !discountUsageData && !discountUsageError;

  const combinedDiscountData = discounts
    .flatMap(d =>
      d.discountDetails.map(detail => {
        const usage = discountUsageData?.getDiscountUsage.find(
          u => u.discountDetailId === detail.discountDetailId,
        );

        if (
          !d.startDate ||
          !detail.discountDetailId ||
          detail.discount === undefined
        ) {
          return null;
        }

        return {
          startDate: d.startDate,
          endDate: d.endDate ?? '',
          discount: detail.discount,
          discountDetailId: detail.discountDetailId,
          discountUsage: usage?.totalCount ?? 0,
          scopes: d.scopes ?? [],
        };
      }),
    )
    .filter((item): item is CombinedDiscountData => item !== null);

  const handleAddCredits = async (
    values: ParsedDiscountValues,
  ): Promise<void> => {
    const { scopes, ...rest } = values;
    try {
      await createDiscount({
        variables: {
          discountInput: {
            ...rest,
            discountType: DiscountType.programCapCredits,
            scopes: toScopeInputs(scopes),
          },
        },
      });
      setSnackbarState('Credits added successfully', 'success', 3000);
      onClose();
      refetch();
    } catch (error) {
      setSnackbarState('Failed to add credits', 'error', 3000);
    }
  };

  const handleAddCreditsClick = (): void => {
    setDrawerContent(
      <DiscountForm
        clientId={clientId}
        formTitle="Add Program Cap Credits"
        onSubmitCallback={handleAddCredits}
        onCancelCallback={onClose}
        discountToEdit={null}
        currentDiscounts={discounts}
        availablePackages={packagesData?.getAllPackages ?? []}
        submitTitle="Submit"
        defaultType={DiscountType.programCapCredits}
      />,
      undefined,
      undefined,
      'temporary',
    );
  };

  const isCreditsLoading =
    isLoading ||
    discountUsageLoading ||
    isDiscountUsagePending ||
    packagesLoading;
  const hasCreditsError = !!(error || discountUsageError || packagesError);

  const renderCredits = (): JSX.Element | JSX.Element[] => {
    if (isCreditsLoading) {
      return <CreditRowSkeleton />;
    }

    if (hasCreditsError) {
      return (
        <HHError
          body="Error fetching program cap credits"
          hhVariant="variant-bypass"
        />
      );
    }

    if (!combinedDiscountData.length) {
      return (
        <HHStack alignItems="center" justifyContent="center">
          <HHTypography hhVariant="body1">No credits</HHTypography>
        </HHStack>
      );
    }

    return combinedDiscountData.map((discount, index) => (
      <HHStack
        key={discount.discountDetailId}
        direction="row"
        spacing={4}
        width="100%"
        alignItems="center"
      >
        <HHStack flex={2}>
          <HHTypography hhVariant="body1" color="textSecondary">
            Effective Dates
          </HHTypography>
          <HHTypography hhVariant="body2">
            {computeEffectiveRange(discount.startDate, discount.endDate)}
          </HHTypography>
        </HHStack>
        <HHStack flex={1}>
          <ScopeDisplay scopes={discount.scopes} />
        </HHStack>
        <HHStack flex={1}>
          <HHTypography hhVariant="body1" color="textSecondary">
            Credit Usage
          </HHTypography>
          <HHTypography hhVariant="body2">
            {discount.discountUsage.toLocaleString()} /{' '}
            {discount.discount.toLocaleString()}
          </HHTypography>
        </HHStack>
        {index < combinedDiscountData.length - 1 && <HHDivider />}
      </HHStack>
    ));
  };

  return (
    <HHStack
      padding={4}
      spacing={3}
      sx={{ backgroundColor: theme => theme.palette.background.default }}
    >
      <HHStack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <HHTypography hhVariant="subtitle1">Program Cap Credits</HHTypography>
        {canWrite && (
          <HHButton
            onClick={handleAddCreditsClick}
            hhVariant="contained"
            size="small"
            disabled={isDisabled || isCreditsLoading}
          >
            Add Credits
          </HHButton>
        )}
      </HHStack>
      <HHDivider />
      {renderCredits()}
    </HHStack>
  );
};
